import { useState } from 'react';
import { Link, Navigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { User, CreditCard, Loader2, ExternalLink } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { createPortalSession } from '../services/stripe';
import { cn } from '../utils/cn';

export default function AccountPage() {
  const { user, profile, isAuthenticated, loading, requestsRemaining, hasUnlimitedAccess } = useAuth();
  const [portalLoading, setPortalLoading] = useState(false);
  const [error, setError] = useState(null);

  const handleManageSubscription = async () => {
    setPortalLoading(true);
    setError(null);
    try {
      await createPortalSession(user.id);
    } catch (err) {
      console.error('Portal error:', err);
      setError(err.message);
      setPortalLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const subscriptionTier = profile?.subscription_tier || 'free_trial';
  const isPaidPlan = subscriptionTier === 'pro' || subscriptionTier === 'advanced';

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-2xl mx-auto px-4 py-16">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/" className="text-3xl font-display font-bold text-primary">
            AskAIQ
          </Link>
          <h1 className="text-display font-display text-primary mt-8 mb-4">
            Account Settings
          </h1>
        </div>

        {/* Account Info Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-card rounded-xl shadow-card p-8 border border-border mb-6"
        >
          <div className="flex items-center gap-4 mb-6">
            <div className="w-12 h-12 rounded-full bg-secondary flex items-center justify-center">
              <User className="w-6 h-6 text-secondary-foreground" />
            </div>
            <div>
              <p className="font-medium text-primary">{user.email}</p>
              <p className="text-sm text-muted-foreground">
                Member since {new Date(user.created_at).toLocaleDateString()}
              </p>
            </div>
          </div>

          <div className="border-t border-border pt-6 space-y-4">
            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Current Plan</span>
              <span className={cn(
                "px-3 py-1 rounded-full text-sm font-medium capitalize",
                subscriptionTier === 'free_trial' && "bg-muted text-muted-foreground",
                subscriptionTier === 'pro' && "bg-secondary/10 text-secondary",
                subscriptionTier === 'advanced' && "bg-accent/10 text-accent"
              )}>
                {subscriptionTier.replace('_', ' ')}
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-muted-foreground">Requests Remaining</span>
              <span className="font-medium text-primary">
                {hasUnlimitedAccess ? 'Unlimited' : requestsRemaining}
              </span>
            </div>

            {profile?.requests_limit && !hasUnlimitedAccess && (
              <div className="flex justify-between items-center">
                <span className="text-muted-foreground">Monthly Limit</span>
                <span className="font-medium text-primary">{profile.requests_limit}</span>
              </div>
            )}
          </div>
        </motion.div>

        {/* Subscription Management Card */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.1 }}
          className="bg-card rounded-xl shadow-card p-8 border border-border"
        >
          <div className="flex items-center gap-3 mb-6">
            <CreditCard className="w-5 h-5 text-muted-foreground" />
            <h2 className="text-lg font-display font-bold text-primary">Subscription</h2>
          </div>

          {error && (
            <div className="mb-4 p-3 rounded-lg bg-error/10 text-error text-sm">
              {error}
            </div>
          )}

          {isPaidPlan ? (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                Manage your subscription, update payment methods, or cancel your plan through Stripe's secure portal.
              </p>
              <button
                onClick={handleManageSubscription}
                disabled={portalLoading}
                className={cn(
                  "w-full py-3 rounded-lg font-medium",
                  "bg-secondary text-secondary-foreground",
                  "hover:bg-secondary/90 disabled:opacity-50",
                  "transition-all flex items-center justify-center gap-2"
                )}
              >
                {portalLoading ? (
                  <>
                    <Loader2 className="w-5 h-5 animate-spin" />
                    Opening Portal...
                  </>
                ) : (
                  <>
                    Manage Subscription
                    <ExternalLink className="w-4 h-4" />
                  </>
                )}
              </button>
              <p className="text-xs text-muted-foreground text-center">
                You can cancel your subscription anytime. Access continues until the end of your billing period.
              </p>
            </div>
          ) : (
            <div className="space-y-4">
              <p className="text-muted-foreground">
                You're currently on the free trial. Upgrade to get more research requests each month.
              </p>
              <Link
                to="/pricing"
                className={cn(
                  "w-full py-3 rounded-lg font-medium",
                  "bg-secondary text-secondary-foreground",
                  "hover:bg-secondary/90",
                  "transition-all flex items-center justify-center gap-2"
                )}
              >
                View Plans
              </Link>
            </div>
          )}
        </motion.div>

        {/* Back link */}
        <div className="mt-8 text-center">
          <Link
            to="/"
            className="text-sm text-muted-foreground hover:text-primary transition-colors"
          >
            Back to Research
          </Link>
        </div>
      </div>
    </div>
  );
}
